// Admin dashboard API client. Same wire protocol as the game, but uses
// the admin JWT (login → /auth/login → token).

const API_BASE_KEY = 'wsb_admin_api_base';
const TOKEN_KEY = 'wsb_admin_token';

export function apiBase(): string {
  return localStorage.getItem(API_BASE_KEY) || (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787');
}
export function setApiBase(url: string) {
  localStorage.setItem(API_BASE_KEY, url);
}
export function token(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const t = token();
  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.body && !(init.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  if (t) headers.set('authorization', `Bearer ${t}`);
  const res = await fetch(`${apiBase()}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as T;
}

// ===== Types =====
export interface EventRow {
  id: number;
  name: string;
  starts_at: number;
  ends_at: number | null;
  active: number;
  in_game_pin: string;
  bg_image_url: string | null;
  logo_image_url: string | null;
  title_image_url: string | null;
}
export interface PrizeRow {
  id: number;
  event_id: number;
  name: string;
  emoji: string;
  image_url: string | null;
  win_rate: number;
  max_qty: number;
  active: number;
  sort_order: number;
  qty_remaining: number;
}
export interface DeviceRow {
  id: number;
  event_id: number | null;
  name: string;
  last_seen_at: number | null;
  created_at: number;
}
export interface SpinRow {
  id: number;
  event_id: number;
  device_id: number;
  prize_id: number | null;
  won: number;
  created_at: number;
  prize_name: string | null;
  device_name: string | null;
}

// ===== API surface =====
export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; user: { id: number; email: string; role: string } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  bootstrap: () => request<{ ok: boolean }>('/auth/bootstrap', { method: 'POST' }),

  events: {
    list: () => request<{ events: EventRow[] }>('/events'),
    create: (b: Partial<EventRow>) =>
      request<{ event: EventRow }>('/events', { method: 'POST', body: JSON.stringify(b) }),
    update: (id: number, b: Partial<EventRow>) =>
      request<{ event: EventRow }>(`/events/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
  },

  prizes: {
    list: (eventId: number) => request<{ prizes: PrizeRow[] }>(`/prizes?event_id=${eventId}`),
    create: (b: Partial<PrizeRow> & { event_id: number; name: string }) =>
      request<{ prize: PrizeRow }>('/prizes', { method: 'POST', body: JSON.stringify(b) }),
    update: (id: number, b: Partial<PrizeRow>) =>
      request<{ prize: PrizeRow }>(`/prizes/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    remove: (id: number) => request<{ ok: true }>(`/prizes/${id}`, { method: 'DELETE' }),
    restock: (id: number) => request<{ ok: true }>(`/prizes/${id}/restock`, { method: 'POST' }),
  },

  devices: {
    list: () => request<{ devices: DeviceRow[] }>('/devices'),
    create: (b: { name: string; event_id?: number }) =>
      request<{ device: DeviceRow; token: string }>('/devices', { method: 'POST', body: JSON.stringify(b) }),
    update: (id: number, b: { name?: string; event_id?: number | null }) =>
      request<{ device: DeviceRow }>(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    remove: (id: number) => request<{ ok: true }>(`/devices/${id}`, { method: 'DELETE' }),
  },

  spins: {
    list: (q: { event_id?: number; limit?: number; offset?: number }) => {
      const p = new URLSearchParams();
      if (q.event_id) p.set('event_id', String(q.event_id));
      if (q.limit) p.set('limit', String(q.limit));
      if (q.offset) p.set('offset', String(q.offset));
      return request<{ spins: SpinRow[] }>(`/spins?${p}`);
    },
    csvUrl: (eventId?: number) => {
      const p = new URLSearchParams();
      if (eventId) p.set('event_id', String(eventId));
      return `${apiBase()}/spins/export.csv?${p}`;
    },
  },

  images: {
    upload: (file: File, folder = 'prize') => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder);
      return request<{ key: string; url: string }>('/images/upload', { method: 'POST', body: fd });
    },
  },
};
